<template>
  <div class="report-overlay" @click.self="$emit('close')">
    <div class="report-panel">
      <button class="report-panel__close" @click="$emit('close')" aria-label="Cerrar">
        <span class="mdi mdi-close"></span>
      </button>

      <h3 class="report-panel__title">Exportar reporte</h3>
      <div class="report-panel__steps">Paso {{ step }} de 4</div>

      <!-- Paso 1: tipo de reporte -->
      <div v-if="step === 1" class="report-panel__step">
        <div
          v-for="opt in REPORT_TYPES"
          :key="opt.value"
          class="report-type-card"
          :class="{ 'report-type-card--active': form.reportType === opt.value }"
          @click="form.reportType = opt.value"
        >
          <span class="report-type-card__name">{{ opt.name }}</span>
          <span class="report-type-card__audience">{{ opt.audience }}</span>
          <span class="report-type-card__desc">{{ opt.desc }}</span>
        </div>
      </div>

      <!-- Paso 2: periodo -->
      <div v-if="step === 2" class="report-panel__step">
        <div class="report-panel__pills">
          <button
            v-for="pt in ['MONTH', 'QUARTER', 'YEAR']"
            :key="pt"
            class="report-panel__pill"
            :class="{ 'report-panel__pill--active': form.periodType === pt }"
            @click="form.periodType = pt"
          >
            {{ { MONTH: 'Mes específico', QUARTER: 'Trimestre', YEAR: 'Año completo' }[pt] }}
          </button>
        </div>

        <div class="report-panel__period-fields">
          <input v-model.number="form.year" type="number" class="report-panel__input" placeholder="Año" />
          <select v-if="form.periodType === 'MONTH'" v-model.number="form.value" class="report-panel__input">
            <option v-for="(m, i) in MONTHS" :key="i" :value="i + 1">{{ m }}</option>
          </select>
          <select v-if="form.periodType === 'QUARTER'" v-model.number="form.value" class="report-panel__input">
            <option v-for="q in [1, 2, 3, 4]" :key="q" :value="q">Q{{ q }}</option>
          </select>
        </div>

        <p class="report-panel__confirm-text">{{ periodConfirmText }}</p>
      </div>

      <!-- Paso 3: personalización -->
      <div v-if="step === 3" class="report-panel__step">
        <span class="report-panel__group-title">Contenido a incluir</span>
        <label v-for="sec in sectionsForType" :key="sec.key" class="report-panel__toggle">
          <input type="checkbox" v-model="form.sections[sec.key]" />
          {{ sec.label }}
        </label>

        <span class="report-panel__group-title">Identidad</span>
        <label
          class="report-panel__toggle"
          :class="{ 'report-panel__toggle--disabled': !hasLogo }"
          :title="hasLogo ? '' : 'Sube un logo en Configuración para poder incluirlo'"
        >
          <input type="checkbox" v-model="form.includeLogo" :disabled="!hasLogo" />
          Incluir logo del parque{{ hasLogo ? '' : ' (sin logo configurado)' }}
        </label>
        <input v-model="form.recipientName" class="report-panel__input" placeholder="Preparado para (opcional)" />
        <textarea v-model="form.notes" class="report-panel__textarea" placeholder="Notas del periodo (opcional)"></textarea>

        <span class="report-panel__group-title">Formato de salida</span>
        <div class="report-panel__format-row">
          <label class="report-panel__toggle"><input type="radio" value="PDF" v-model="form.format" /> PDF</label>
          <label class="report-panel__toggle report-panel__toggle--disabled">
            <input type="radio" value="EXCEL" disabled /> Excel (próximamente)
          </label>
        </div>
      </div>

      <!-- Paso 4: generar -->
      <div v-if="step === 4" class="report-panel__step">
        <div class="report-panel__summary-box">
          <p><strong>{{ selectedReportName }}</strong></p>
          <p class="muted">{{ periodConfirmText }}</p>
          <p class="muted">{{ activeSectionsCount }} secciones incluidas</p>
        </div>

        <div class="report-panel__actions">
          <button class="report-panel__btn" :disabled="generating" @click="handleGenerate('preview')">
            {{ generating ? 'Generando...' : 'Previsualizar' }}
          </button>
          <button class="report-panel__btn report-panel__btn--primary" :disabled="generating" @click="handleGenerate('download')">
            {{ generating ? 'Generando...' : 'Generar y descargar' }}
          </button>
        </div>

        <div class="report-panel__send">
          <span class="report-panel__group-title">Enviar por correo</span>
          <input v-model="form.sendTo" type="email" class="report-panel__input" placeholder="Correo del destinatario" />
          <button class="report-panel__btn" :disabled="generating || !sendToValid" @click="handleGenerate('email')">
            {{ generating ? 'Enviando...' : 'Generar y enviar por correo' }}
          </button>
          <p v-if="sentNotice" class="report-panel__sent">{{ sentNotice }}</p>
          <p v-if="sendError" class="report-panel__error">{{ sendError }}</p>
        </div>

        <div class="report-panel__history">
          <span class="report-panel__group-title">Reportes anteriores</span>
          <div v-if="history.length === 0" class="muted">Sin reportes generados aún.</div>
          <div v-for="r in history" :key="r.id" class="report-history-row">
            <span>{{ REPORT_TYPES.find((t) => t.value === r.reportType)?.name }} · {{ r.periodLabel }}</span>
            <span class="report-history-row__actions">
              <a :href="r.downloadUrl" target="_blank" class="report-history-row__link">Descargar</a>
              <button
                class="report-history-row__delete"
                title="Eliminar reporte"
                :disabled="deletingId === r.id"
                @click="onDeleteReport(r.id)"
              >
                <span class="mdi mdi-close"></span>
              </button>
            </span>
          </div>
        </div>
      </div>

      <div class="report-panel__nav">
        <button v-if="step > 1" class="report-panel__nav-btn" @click="step--">Atrás</button>
        <button v-if="step < 4" class="report-panel__nav-btn report-panel__nav-btn--primary" @click="step++">Continuar</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { generateReport, getReportHistory, deleteReport } from '@/services/reports'

const props = defineProps({
  parkId: { type: String, required: true },
  hasLogo: { type: Boolean, default: false },
})

defineEmits(['close'])

const REPORT_TYPES = [
  { value: 'BANK', name: 'Reporte de Desempeño Financiero', audience: 'Banco / institución financiera', desc: 'Cobertura de deuda, flujo de caja y calidad de inquilinos.' },
  { value: 'INVESTOR', name: 'Reporte de Desempeño del Activo', audience: 'Fondo / FIBRA / accionistas', desc: 'NOI, ocupación histórica, WALE y concentración de inquilinos.' },
  { value: 'DIRECTOR', name: 'Resumen Ejecutivo Mensual', audience: 'Director general / dueño', desc: 'Resumen de 5 minutos con lo que requiere tu atención.' },
]

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

const SECTIONS_BY_TYPE = {
  DIRECTOR: [
    { key: 'summary', label: 'Resumen del mes y situaciones críticas' },
    { key: 'criticalContracts', label: 'Detalle de contratos críticos' },
    { key: 'expenses', label: 'Gastos del mes' },
    { key: 'projection', label: 'Proyección próximos 3 meses' },
  ],
  BANK: [
    { key: 'summary', label: 'Resumen ejecutivo' },
    { key: 'incomeDetail', label: 'Detalle de ingresos por inquilino' },
    { key: 'incomeStatement', label: 'Estado de resultados operativo' },
    { key: 'leaseExpiry', label: 'Vencimientos próximos 12 meses' },
  ],
  INVESTOR: [
    { key: 'summary', label: 'Dashboard de métricas clave' },
    { key: 'tenantAnalysis', label: 'Análisis de inquilinos' },
    { key: 'noiTrend', label: 'Tendencia de NOI' },
    { key: 'leasePipeline', label: 'Pipeline de vencimientos' },
  ],
}

const step = ref(1)
const generating = ref(false)
const history = ref([])
const deletingId = ref(null)

const now = new Date()
const form = reactive({
  reportType: 'DIRECTOR',
  periodType: 'MONTH',
  year: now.getFullYear(),
  value: now.getMonth() + 1,
  sections: { summary: true, criticalContracts: true, incomeDetail: true, incomeStatement: true, leaseExpiry: true, tenantAnalysis: true, noiTrend: true, leasePipeline: true, expenses: true, projection: true },
  includeLogo: false,
  recipientName: '',
  notes: '',
  format: 'PDF',
  sendTo: '',
})
const sentNotice = ref('')
const sendError = ref('')
const sendToValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.sendTo.trim()))

const selectedReportName = computed(() => REPORT_TYPES.find((t) => t.value === form.reportType)?.name || '')
const sectionsForType = computed(() => SECTIONS_BY_TYPE[form.reportType] || [])
const activeSectionsCount = computed(() => sectionsForType.value.filter((s) => form.sections[s.key]).length)

const periodConfirmText = computed(() => {
  if (form.periodType === 'MONTH') return `Este reporte incluirá datos de ${MONTHS[form.value - 1]} ${form.year}.`
  if (form.periodType === 'QUARTER') return `Este reporte incluirá datos de Q${form.value} ${form.year}.`
  return `Este reporte incluirá datos del año completo ${form.year}.`
})

async function loadHistory() {
  history.value = await getReportHistory(props.parkId)
}

async function onDeleteReport(reportId) {
  deletingId.value = reportId
  try {
    await deleteReport(props.parkId, reportId)
    history.value = history.value.filter((r) => r.id !== reportId)
  } catch (e) {
    // fallo silencioso: si no se pudo borrar, el historial simplemente no cambia
  } finally {
    deletingId.value = null
  }
}

async function handleGenerate(mode = 'download') {
  generating.value = true
  sentNotice.value = ''
  sendError.value = ''
  try {
    const sending = mode === 'email'
    const result = await generateReport(props.parkId, {
      reportType: form.reportType,
      periodType: form.periodType,
      year: form.year,
      value: form.value,
      recipientName: form.recipientName,
      notes: form.notes,
      format: form.format,
      includeLogo: props.hasLogo && form.includeLogo,
      sendTo: sending ? form.sendTo.trim() : undefined,
    })
    if (sending) {
      sentNotice.value = result.emailQueued ? `Reporte enviado a ${result.emailTo}.` : 'Reporte generado.'
    } else {
      window.open(result.downloadUrl, '_blank')
    }
    await loadHistory()
  } catch (e) {
    if (mode === 'email') sendError.value = e.message || 'No se pudo enviar el reporte'
  } finally {
    generating.value = false
  }
}

onMounted(loadHistory)
</script>

<style scoped>
.report-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: flex-end;
  z-index: 60;
}

.report-panel {
  position: relative;
  width: 420px;
  max-width: 92vw;
  height: 100%;
  background: var(--bg-surface-1);
  border-left: 1px solid var(--border-active);
  padding: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.report-panel__close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1.1rem;
}

.report-panel__title {
  font-family: var(--font-display);
  font-size: 1.05rem;
  color: #1e3a5f;
}

.report-panel__steps {
  font-size: 0.7rem;
  color: #94a3b8;
}

.report-panel__step {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
}

.report-type-card {
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.report-type-card--active {
  border-color: var(--accent-blue);
  background: rgba(61, 155, 255, 0.08);
}

.report-type-card__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1e3a5f;
}

.report-type-card__audience {
  font-size: 0.68rem;
  color: var(--accent-blue);
}

.report-type-card__desc {
  font-size: 0.7rem;
  color: #64748b;
}

.report-panel__pills {
  display: flex;
  gap: 0.4rem;
}

.report-panel__pill {
  flex: 1;
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #64748b;
  font-size: 0.68rem;
  padding: 0.35rem 0.5rem;
  cursor: pointer;
}

.report-panel__pill--active {
  background: rgba(61, 155, 255, 0.15);
  border-color: var(--accent-blue);
  color: var(--accent-blue);
}

.report-panel__period-fields {
  display: flex;
  gap: 0.5rem;
}

.report-panel__input {
  flex: 1;
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #1e3a5f;
  font-size: 0.75rem;
  padding: 0.4rem 0.6rem;
}

.report-panel__textarea {
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #1e3a5f;
  font-size: 0.75rem;
  padding: 0.4rem 0.6rem;
  min-height: 60px;
  resize: none;
}

.report-panel__confirm-text {
  font-size: 0.72rem;
  color: #64748b;
}

.report-panel__group-title {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #94a3b8;
  margin-top: 0.4rem;
}

.report-panel__toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  color: #64748b;
}

.report-panel__toggle--disabled {
  opacity: 0.5;
}

.report-panel__format-row {
  display: flex;
  gap: 1rem;
}

.report-panel__summary-box {
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
  font-size: 0.78rem;
  color: #1e3a5f;
}

.muted {
  color: #64748b;
  font-size: 0.7rem;
}

.report-panel__actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.report-panel__btn {
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #1e3a5f;
  font-size: 0.75rem;
  padding: 0.5rem;
  cursor: pointer;
}

.report-panel__btn--primary {
  background: var(--accent-blue);
  border-color: var(--accent-blue);
  color: white;
}

.report-panel__send { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 1rem; }
.report-panel__sent { margin: 0; font-size: 12px; color: #15803d; }
.report-panel__error { margin: 0; font-size: 12px; color: #dc2626; }
.report-panel__btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.report-panel__history {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.report-history-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.72rem;
  color: #64748b;
  border-bottom: 1px solid var(--border-subtle);
  padding: 0.3rem 0;
}

.report-history-row__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.report-history-row__link {
  color: var(--accent-blue);
}

.report-history-row__delete {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 0.85rem;
  line-height: 1;
  padding: 0.15rem;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.12s ease, color 0.12s ease;
}

.report-history-row__delete:hover {
  background: #fee2e2;
  color: #e84545;
}

.report-history-row__delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.report-panel__nav {
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 0.8rem;
  border-top: 1px solid var(--border-subtle);
}

.report-panel__nav-btn {
  background: none;
  border: 1px solid var(--border-active);
  border-radius: 6px;
  color: #64748b;
  font-size: 0.75rem;
  padding: 0.4rem 0.9rem;
  cursor: pointer;
}

.report-panel__nav-btn--primary {
  background: var(--accent-blue);
  border-color: var(--accent-blue);
  color: white;
  margin-left: auto;
}
</style>